import { useState, FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getApiUrl } from '../config/api';

export function PartnerAccountPage() {
  const { user } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [nameMessage, setNameMessage] = useState('');
  const [savingName, setSavingName] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSaveName = async (e: FormEvent) => {
    e.preventDefault();
    setNameMessage('');
    if (!name.trim()) return;

    setSavingName(true);
    try {
      const response = await fetch(getApiUrl('/api/users/me/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim() }),
      });
      if (response.ok) {
        setNameMessage('Name updated — it will show after your next sign in');
      } else {
        const data = await response.json();
        setNameMessage(data.error || 'Failed to update name');
      }
    } catch {
      setNameMessage('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch(getApiUrl('/api/users/me/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setPasswordMessage('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(data.error || 'Failed to change password');
      }
    } catch {
      setPasswordError('Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">My Account</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">
          Manage your login details
        </p>
      </div>

      {/* Profile */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-4">Profile</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-dark-text mb-2">Email</label>
          <p className="text-dark-text-secondary">{user?.email}</p>
        </div>

        <form onSubmit={handleSaveName} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-dark-text mb-2">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="input w-full"
              required
            />
          </div>
          {nameMessage && <p className="text-sm text-dark-text-secondary">{nameMessage}</p>}
          <button type="submit" disabled={savingName} className="btn btn-primary">
            {savingName ? 'Saving...' : 'Save Name'}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div className="card p-6">
        <h2 className="text-lg font-bold text-white mb-4">Change Password</h2>

        {passwordError && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-sm">
            {passwordError}
          </div>
        )}
        {passwordMessage && (
          <div className="mb-4 p-4 bg-green-900/20 border border-green-700 rounded-lg text-green-400 text-sm">
            {passwordMessage}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label htmlFor="current" className="block text-sm font-medium text-dark-text mb-2">
              Current Password
            </label>
            <input
              id="current"
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              className="input w-full"
              required
              autoComplete="current-password"
            />
          </div>
          <div>
            <label htmlFor="new" className="block text-sm font-medium text-dark-text mb-2">
              New Password
            </label>
            <input
              id="new"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="input w-full"
              placeholder="Min 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="confirmNew" className="block text-sm font-medium text-dark-text mb-2">
              Confirm New Password
            </label>
            <input
              id="confirmNew"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="input w-full"
              required
              autoComplete="new-password"
            />
          </div>
          <button type="submit" disabled={savingPassword} className="btn btn-primary">
            {savingPassword ? 'Saving...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
